import clsx from 'clsx'
import { differenceInDays } from 'date-fns'
import isNull from 'lodash/isNull'
import map from 'lodash/map'
import noop from 'lodash/noop'
import React, { FC, memo, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgEdit from '@Assets/SVG/Edit'
import LockIcon from '@Assets/SVG/Lock'
import { Button } from '@Components/Button'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { useSavedCheckoutLocalData } from '@Domains/Checkout/Hooks/useSavedCheckoutLocalData'
import { AdyenContext2 } from '@Domains/Checkout/Providers/AdyenContextProvider'
import { CardTypeIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import { PaymentProviderBusContext, PaymentProviders } from '@Domains/Checkout/Steps/Payment/PaymentProviderFactory/PaymentProviderBus'
import { buildAdyenComponent } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyen'
import {
  useAdyenCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Adyen/Hooks/useAdyenCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { useTranslation } from '@Globals/Translations'

import styles from './AdyenCreditCard.module.scss'

enum ButtonState {
  Submit = 'Submit',
  Idle = 'Idle',
}

interface CardInputError {
  encryptedCardNumber?: unknown
  encryptedExpiryDate?: unknown
  encryptedSecurityCode?: unknown
}

interface CardPaymentProps {
  brandImageUrl?: string
  cardBrand: string
  cardLastDigits: string
  formData: unknown
  isValid: boolean
  timeStamp: number
  type: string
}

function isTimestampLessThenOneDay(timestamp: number): boolean {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))
  const dateToCompare = new Date(new Date(timestamp).toLocaleString('en-US', { timeZone: 'Europe/Copenhagen' }))
  const difference = differenceInDays(now, dateToCompare)
  return difference < 1
}

export const AdyenCreditCardFormComponent: FC<CheckoutPaymentComponentProps> = memo(({
  option,
  submitPayment,
}) => {
  const { t } = useTranslation()
  const { adyenConfig } = useContext(AdyenContext2)
  const errorsRef = useRef<null | CardInputError>(null)

  const userInputError = useCallback((errorName) => {
    ampli.userInputError({
      errors: errorName,
    })
  }, [])
  const handleErrors = useCallback((field: null | { fieldType: string }) => {
    if ((!field || field.fieldType === 'encryptedExpiryDate') && errorsRef.current?.encryptedExpiryDate) {
      userInputError('cof_credit_card_expiration_date')
    }
    if ((!field || field.fieldType === 'encryptedCardNumber') && errorsRef.current?.encryptedCardNumber) {
      userInputError('cof_credit_card_number')
    }
    if ((!field || field.fieldType === 'encryptedSecurityCode') && errorsRef.current?.encryptedSecurityCode) {
      userInputError('cof_credit_card_cvc')
    }
  }, [userInputError])
  const handleCardErrors = useCallback((data) => {
    if (data.errors) {
      errorsRef.current = data.errors
    }
    if (document?.activeElement instanceof HTMLButtonElement) {
      setTimeout(() => {
        handleErrors(null)
      }, 100)
    }
  }, [handleErrors])

  const {
    busData,
    setBusData,
  } = useContext(PaymentProviderBusContext)(PaymentProviders.AdyenCreditCard)
  const { adyenInstance } = useContext(AdyenContext2)
  const ref = useRef<HTMLDivElement | null>(null)
  const {
    checkoutLocal,
    saveCheckoutLocal,
  } = useSavedCheckoutLocalData()
  const [prevPaymentData, setPrevPaymentData] = useState<null | CardPaymentProps>(null)
  const [fieldValid, setFieldValid] = useState<unknown>(null)
  const [selectedBrand, setSelectedBrand] = useState<unknown>(null)
  const { handleAdyenResponse } = useAdyenCheckoutResponse()
  const [adyenComponent, setAdyenComponent] = useState(null)

  useEffect(() => {
    if (adyenComponent) {
      return
    }
    if (prevPaymentData !== null) {
      return
    }
    const component = buildAdyenComponent(adyenInstance, option.type === 'credit_card' ? 'scheme' : option.type, ref, adyenConfig, {
      onChange: handleCardErrors,
      onBlur: (field) => {
        setTimeout(() => {
          handleErrors(field)
        }, 100)
      },
      paymentMethods: adyenConfig?.paymentMethodsResponse?.paymentMethods,
      onFieldValid: (newFieldValid) => {
        setFieldValid(prev => (isNull(prev) ? newFieldValid : { ...prev!, ...newFieldValid }))
      },
      onBrand: setSelectedBrand,
      onSubmit: () => {
        noop()
      },
    })
    setAdyenComponent(component)
  }, [prevPaymentData, adyenComponent, adyenInstance, handleCardErrors, handleErrors, option, adyenConfig])

  useEffect(() => {
    const data = checkoutLocal
    if (data?.savedPaymentInputData) {
      const prev = (data.savedPaymentInputData as CardPaymentProps)
      const canSetPrevious = isTimestampLessThenOneDay(prev.timeStamp) && prev.type === 'credit_card' && prev.cardLastDigits !== null
      setPrevPaymentData(canSetPrevious ? prev : null)
    }
  }, [checkoutLocal])

  useEffect(() => {
    if (busData === ButtonState.Submit) {
      setBusData(ButtonState.Idle)
      // @ts-ignore
      const cardBrand = selectedBrand?.brand || null
      // @ts-ignore
      const brandImageUrl = selectedBrand?.brandImageUrl || null
      // @ts-ignore
      const cardLastDigits = fieldValid?.endDigits || null
      let paymentData: CardPaymentProps | null = prevPaymentData
      if (!prevPaymentData && adyenComponent) {
        paymentData = {
          isValid: (adyenComponent as ({ isValid: boolean })).isValid,
          cardBrand,
          cardLastDigits,
          type: 'credit_card',
          timeStamp: Date.now(),
          formData: (adyenComponent as ({ data: unknown })).data,
        }
      }
      if (paymentData && paymentData.isValid) {
        saveCheckoutLocal({
          savedPaymentInputData: {
            ...paymentData,
            brandImageUrl,
          },
        })
        setPrevPaymentData(paymentData)
      }
      if (paymentData?.isValid) {
        submitPayment(
          paymentData.formData,
          response => handleAdyenResponse(adyenInstance, response),
        )
      }
      if (!paymentData?.isValid && !!(adyenComponent as any)?.submit) {
        (adyenComponent as any).submit()
      }
    }
  }, [busData, selectedBrand, fieldValid, setBusData, prevPaymentData, saveCheckoutLocal, adyenComponent, submitPayment, handleAdyenResponse, adyenInstance])

  return (
    <div key={`form_${option.id}`} className={styles.creditCardWrapper}>
      <div className={styles.payInfo}>
        <p className={styles.payInfoHeader}>{t('checkout:COF_FILL_OUT_PAYMENT_INFO')}</p>
        <span className={clsx(styles.safeInfo, prevPaymentData && styles.hasOldCardData)}>
          <SVGIcon icon={LockIcon} size="18px" />
          {t('checkout:PAYMENT_SAFE_TEXT')}
        </span>
      </div>
      <div className={clsx(prevPaymentData && styles.hasOldCardData)}>
        <div ref={ref} />
        <div className={styles.cardIconsWrapper}>
          <span>{`${t('checkout:ACCEPTED_CARDS')}:`}</span>
          <div className={styles.cardIcons}>
            {map(option.icons, icon => <img key={icon.name} alt={icon.name} src={icon.logo?.url} />)}
          </div>
        </div>
      </div>
      {prevPaymentData && (
        <div className={styles.prevPaymentWrapper}>
          {prevPaymentData.brandImageUrl
            ? <img alt={prevPaymentData.cardBrand} src={prevPaymentData.brandImageUrl} />
            : <CardTypeIcon cardType={prevPaymentData.cardBrand} className={styles.cardBrandIco} />}
          <p>{`${t('checkout:ENDING_WITH')} ${prevPaymentData.cardLastDigits}`}</p>
          {/* TODO: use translation for aria-label */}
          <button aria-label="Edit" onClick={() => setPrevPaymentData(null)} type="button">
            <SvgEdit />
          </button>
        </div>
      )}
    </div>
  )
})

export const AdyenCreditCardButton: FC<CheckoutPaymentComponentProps> = ({ option }) => {
  const { t } = useTranslation()
  const { setBusData } = useContext(PaymentProviderBusContext)(PaymentProviders.AdyenCreditCard)
  return (
    <div key={option.id} className={styles.payButtonWrapper}>
      <Button
        className={clsx(styles.payButton)}
        onClick={() => {
          setBusData(ButtonState.Submit)
        }}
        type="button"
      >
        {t('checkout:COF_GO_TO_SECURE_PAYMENT')}
      </Button>
    </div>
  )
}
